import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { InstructorAuthProvider } from '../context/InstructorAuthContext';
import { InstructorProtectedRoute } from '../components/instructor/InstructorProtectedRoute';

// Pages d'authentification
import UnifiedAuthPage from '../pages/auth/UnifiedAuthPage';
import InstructorUnauthorizedPage from '../pages/instructor/InstructorUnauthorizedPage';

// Pages principales
import InstructorDashboardPage from '../pages/instructor/InstructorDashboardPage';
import InstructorCoursesPage from '../pages/instructor/InstructorCoursesPage';
import InstructorCourseFormPage from '../pages/instructor/InstructorCourseFormPage';
import InstructorStudentsPage from '../pages/instructor/InstructorStudentsPage';
import InstructorAnalyticsPage from '../pages/instructor/InstructorAnalyticsPage';
import InstructorProfilePage from '../pages/instructor/InstructorProfilePage';
import InstructorCalendarPage from '../pages/instructor/InstructorCalendarPage';
import InstructorMessagesPage from '../pages/instructor/InstructorMessagesPage';

// Permissions requises pour chaque route
export const INSTRUCTOR_ROUTE_PERMISSIONS = {
  DASHBOARD: [], // Accès de base pour tous les instructeurs
  // Les pages listes doivent reposer sur les permissions "view_*" par défaut
  COURSES: ['view_courses'],
  CREATE_COURSE: ['create_courses'],
  EDIT_COURSE: ['edit_own_courses'],
  STUDENTS: ['view_students'],
  // Analytics peut rester spécifique si attribuée, sinon ajouterez la permission au besoin côté DB
  ANALYTICS: ['analytics_view'],
  // Le profil doit rester accessible à tout instructeur authentifié
  PROFILE: [],
  LIVE_SESSIONS: ['live_session_create', 'live_session_manage'],
  ASSESSMENTS: ['assessment_create', 'assessment_grade'],
  COMMUNICATION: ['send_messages', 'student_message', 'bulk_message'],
} as const;

/**
 * Configuration des routes instructeur avec leurs permissions
 */
const InstructorRoutes: React.FC = () => {
  return (
    <InstructorAuthProvider>
      <Routes>
        {/* Routes publiques (non protégées) */}
        <Route path="login" element={<UnifiedAuthPage />} />
        <Route path="unauthorized" element={<InstructorUnauthorizedPage />} />
        
        {/* Routes protégées */}
        <Route path="dashboard" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.DASHBOARD}>
            <InstructorDashboardPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="courses" element={
          <InstructorProtectedRoute 
            requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.COURSES}
            requireAll={false}
          >
            <InstructorCoursesPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="courses/new" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.CREATE_COURSE}>
            <InstructorCourseFormPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="courses/:id/edit" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.EDIT_COURSE}>
            <InstructorCourseFormPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="students" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.STUDENTS}>
            <InstructorStudentsPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="analytics" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.ANALYTICS}>
            <InstructorAnalyticsPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="profile" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.PROFILE}>
            <InstructorProfilePage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="calendar" element={
          <InstructorProtectedRoute requiredPermissions={[]}>
            <InstructorCalendarPage />
          </InstructorProtectedRoute>
        } />
        
        <Route path="messages" element={
          <InstructorProtectedRoute requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.COMMUNICATION}>
            <InstructorMessagesPage />
          </InstructorProtectedRoute>
        } />
        
        {/* Routes futures pour les fonctionnalités avancées */}
        <Route path="/live-sessions/*" element={
          <InstructorProtectedRoute 
            requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.LIVE_SESSIONS}
            requireAll={false}
          >
            <div className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Sessions Live</h2>
              <p className="text-gray-600">Fonctionnalité en cours de développement</p>
            </div>
          </InstructorProtectedRoute>
        } />
        
        <Route path="/assessments/*" element={
          <InstructorProtectedRoute 
            requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.ASSESSMENTS}
            requireAll={false}
          >
            <div className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Évaluations</h2>
              <p className="text-gray-600">Fonctionnalité en cours de développement</p>
            </div>
          </InstructorProtectedRoute>
        } />
        
        <Route path="/messages/*" element={
          <InstructorProtectedRoute 
            requiredPermissions={INSTRUCTOR_ROUTE_PERMISSIONS.COMMUNICATION}
            requireAll={false}
          >
            <div className="p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Messagerie</h2>
              <p className="text-gray-600">Fonctionnalité en cours de développement</p>
            </div>
          </InstructorProtectedRoute>
        } />
        
        {/* Redirection par défaut */}
        <Route path="/" element={<Navigate to="/instructor/dashboard" replace />} />
        <Route path="/*" element={<Navigate to="/instructor/dashboard" replace />} />
      </Routes>
    </InstructorAuthProvider>
  );
};

/**
 * Composant wrapper pour intégrer les routes instructeur dans l'application principale
 */
export const InstructorApp: React.FC = () => {
  return (
    <div className="instructor-app">
      <InstructorRoutes />
    </div>
  );
};

/**
 * Configuration des permissions par fonctionnalité
 */
export const INSTRUCTOR_PERMISSIONS = {
  // Gestion des cours
  COURSE_CREATE: 'course_create',
  COURSE_EDIT: 'course_edit',
  COURSE_DELETE: 'course_delete',
  COURSE_PUBLISH: 'course_publish',
  COURSE_VIEW: 'view_courses',
  
  // Gestion des étudiants
  STUDENT_VIEW: 'view_students',
  STUDENT_GRADE: 'student_grade',
  STUDENT_MESSAGE: 'student_message',
  
  // Sessions live
  LIVE_SESSION_CREATE: 'live_session_create',
  LIVE_SESSION_MANAGE: 'live_session_manage',
  
  // Évaluations
  ASSESSMENT_CREATE: 'assessment_create',
  ASSESSMENT_GRADE: 'assessment_grade',
  
  // Analytics
  ANALYTICS_VIEW: 'analytics_view',
  ANALYTICS_EXPORT: 'analytics_export',
  
  // Communication
  SEND_MESSAGES: 'send_messages',
  BULK_MESSAGE: 'bulk_message',
  ANNOUNCEMENT: 'announcement',
  
  // Administration
  PROFILE_EDIT: 'profile_edit',
  DASHBOARD_VIEW: 'view_dashboard',
  SETTINGS_MANAGE: 'settings_manage'
} as const;

/**
 * Helper pour vérifier les permissions dans les composants
 */
export const checkInstructorPermission = (
  userPermissions: string[], 
  requiredPermissions: string | string[], 
  requireAll: boolean = false
): boolean => {
  const permsArray = Array.isArray(requiredPermissions) 
    ? requiredPermissions 
    : [requiredPermissions];

  if (requireAll) {
    return permsArray.every(perm => userPermissions.includes(perm));
  } else {
    return permsArray.some(perm => userPermissions.includes(perm));
  }
};

/**
 * Configuration des menus de navigation avec permissions
 */
export const INSTRUCTOR_NAVIGATION = [
  {
    name: 'Tableau de bord',
    path: '/instructor/dashboard',
    permissions: [],
    icon: 'BarChart3'
  },
  {
    name: 'Mes cours',
    path: '/instructor/courses',
    permissions: [INSTRUCTOR_PERMISSIONS.COURSE_VIEW],
    icon: 'BookOpen'
  },
  {
    name: 'Étudiants',
    path: '/instructor/students',
    permissions: [INSTRUCTOR_PERMISSIONS.STUDENT_VIEW],
    icon: 'Users'
  },
  {
    name: 'Analytics',
    path: '/instructor/analytics',
    permissions: [INSTRUCTOR_PERMISSIONS.ANALYTICS_VIEW],
    icon: 'TrendingUp'
  },
  {
    name: 'Sessions live',
    path: '/instructor/live-sessions',
    permissions: [INSTRUCTOR_PERMISSIONS.LIVE_SESSION_CREATE, INSTRUCTOR_PERMISSIONS.LIVE_SESSION_MANAGE],
    icon: 'Video'
  },
  {
    name: 'Évaluations',
    path: '/instructor/assessments',
    permissions: [INSTRUCTOR_PERMISSIONS.ASSESSMENT_CREATE, INSTRUCTOR_PERMISSIONS.ASSESSMENT_GRADE],
    icon: 'FileText'
  },
  {
    name: 'Messages',
    path: '/instructor/messages',
    permissions: [INSTRUCTOR_PERMISSIONS.SEND_MESSAGES, INSTRUCTOR_PERMISSIONS.STUDENT_MESSAGE, INSTRUCTOR_PERMISSIONS.BULK_MESSAGE],
    icon: 'MessageSquare'
  },
  {
    name: 'Profil',
    path: '/instructor/profile',
    permissions: [],
    icon: 'User'
  }
] as const;

export default InstructorRoutes;
